import { useCallback, useEffect, useRef, useState } from "react";
import { ClipboardList, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { canManageWorkspaceTeam } from "@/lib/auxiliaryRoleGuide";
import { formatAssignedTaskDueLabel } from "@/lib/assignedTaskDue";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBntAsistenteOptional } from "@/modules/asistente/BntAsistenteContext";
import { focusOrOpenMainSystem } from "@/modules/asistente/bntAsistenteMessaging";
import {
  UNREAD_TASKS_POLL_MS,
  UNREAD_TASKS_REMINDER_MS,
  readLastPromptAt,
  shouldShowUnreadTasksPrompt,
  writeLastPromptAt,
} from "@/modules/asistente/bntAsistenteUnreadTasksReminder";

export {
  UNREAD_TASKS_POLL_MS,
  UNREAD_TASKS_REMINDER_MS,
  shouldShowUnreadTasksPrompt,
} from "@/modules/asistente/bntAsistenteUnreadTasksReminder";

/**
 * Modal centrado para auxiliares: recuerda tareas sin leer.
 * Al avisar: abre Mis tareas + enfoca el sistema completo; cada 2 h hasta confirmar lectura.
 */
export default function BntAsistenteUnreadTasksModal({ onAttention } = {}) {
  const { user } = useAuth();
  const ctx = useBntAsistenteOptional();
  const isAdmin = canManageWorkspaceTeam(user);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const openRef = useRef(false);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const fetchUnread = useCallback(async () => {
    if (!user?.id || isAdmin) return [];
    try {
      const { data } = await api.get("/asistente/assigned-tasks/pending-alerts", { timeout: 15000 });
      if (Array.isArray(data?.unread_items)) return data.unread_items;
      const all = Array.isArray(data?.items) ? data.items : [];
      return all.filter((t) => !t?.aux_read_at);
    } catch {
      return [];
    }
  }, [user?.id, isAdmin]);

  const bringAttention = useCallback(() => {
    try {
      window.focus?.();
    } catch {
      /* ignore */
    }
    // Panel integrado: abrir Mis tareas (sistema completo en esta pestaña).
    ctx?.openPanel?.({ tab: "mis-tareas" });
    // Solo desde ventana compacta / popup: enfocar o abrir la pestaña principal
    // (no llamar window.open desde el Layout o se duplica la ventana).
    const isPopup = Boolean(window.opener && !window.opener.closed);
    if (isPopup) {
      try {
        focusOrOpenMainSystem({ path: "/" });
      } catch {
        /* ignore */
      }
    }
    if (typeof onAttention === "function") {
      try {
        onAttention({ tab: "mis-tareas" });
      } catch {
        /* ignore */
      }
    }
    window.dispatchEvent(
      new CustomEvent("visor:assigned-tasks-unread-attention", {
        detail: { tab: "mis-tareas" },
      }),
    );
  }, [ctx, onAttention]);

  const tryShow = useCallback(async ({ force = false } = {}) => {
    if (!user?.id || isAdmin) return;
    const unread = await fetchUnread();
    setItems(unread);
    if (!unread.length) {
      setOpen(false);
      return;
    }
    const last = readLastPromptAt(user.id);
    if (!shouldShowUnreadTasksPrompt({
      hasUnread: true,
      isAlreadyOpen: openRef.current,
      force,
      lastPromptAt: last,
    })) {
      return;
    }
    writeLastPromptAt(user.id);
    bringAttention();
    setOpen(true);
  }, [user?.id, isAdmin, fetchUnread, bringAttention]);

  useEffect(() => {
    if (!user?.id || isAdmin) return undefined;

    // Entrada a sesión: forzar una vez si hay no leídas.
    void tryShow({ force: true });

    const pollId = window.setInterval(() => {
      void fetchUnread().then((unread) => {
        setItems(unread);
        if (!unread.length) {
          setOpen(false);
          return;
        }
        void tryShow({ force: false });
      });
    }, UNREAD_TASKS_POLL_MS);

    const onChanged = () => {
      void fetchUnread().then((unread) => {
        setItems(unread);
        if (!unread.length) setOpen(false);
      });
    };
    window.addEventListener("visor:assigned-tasks-changed", onChanged);
    return () => {
      window.clearInterval(pollId);
      window.removeEventListener("visor:assigned-tasks-changed", onChanged);
    };
    // Solo al cambiar de usuario / rol.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAdmin]);

  /** Cerrar sin confirmar: no reinicia el reloj de 2 h (sigue contando desde el aviso). */
  const handleDismiss = () => {
    setOpen(false);
  };

  const handleMarkRead = async () => {
    if (!items.length) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await Promise.all(
        items.map((task) =>
          api.patch(`/asistente/assigned-tasks/${task.id}`, { aux_read: true }, { timeout: 15000 }),
        ),
      );
      setItems([]);
      setOpen(false);
      if (user?.id) writeLastPromptAt(user.id);
      ctx?.reload?.();
      window.dispatchEvent(new CustomEvent("visor:assigned-tasks-changed"));
    } catch {
      /* ignore — el auxiliar puede reintentar */
    } finally {
      setBusy(false);
    }
  };

  const handleOpenTasks = () => {
    bringAttention();
    setOpen(false);
  };

  if (!user || isAdmin) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : handleDismiss())}>
      <DialogContent
        className="max-w-md rounded-sm border-[#0F2D5C]/20 z-[60]"
        data-testid="bnt-asistente-unread-tasks-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F2D5C] text-base">
            <ClipboardList className="w-5 h-5 shrink-0" aria-hidden />
            Tareas pendientes por revisar
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Tiene <strong>{items.length}</strong> tarea(s) asignada(s) que aún no ha marcado como leídas.
            Se abre el sistema completo en <strong>Mis tareas</strong> para que confirme la lectura;
            si no, este aviso se repetirá cada 2 horas.
          </DialogDescription>
        </DialogHeader>

        <ul
          className="m-0 p-0 list-none space-y-2 max-h-56 overflow-y-auto"
          data-testid="bnt-asistente-unread-tasks-list"
        >
          {items.slice(0, 8).map((task) => (
            <li
              key={task.id}
              className="rounded-md border border-amber-200/80 bg-amber-50/60 px-3 py-2 text-xs text-slate-800"
            >
              <p className="m-0 whitespace-pre-wrap leading-relaxed line-clamp-3">{task.body}</p>
              {task.due_date ? (
                <p className="m-0 mt-1 text-[10px] text-slate-500">
                  Límite: {formatAssignedTaskDueLabel(task.due_date)}
                </p>
              ) : null}
            </li>
          ))}
          {items.length > 8 ? (
            <li className="text-[11px] text-slate-500 px-1">…y {items.length - 8} más</li>
          ) : null}
        </ul>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-sm text-xs"
            onClick={handleOpenTasks}
            disabled={busy}
            data-testid="bnt-asistente-unread-open-tasks"
          >
            Ir a Mis tareas
          </Button>
          <Button
            type="button"
            className="rounded-sm text-xs bg-[#0F2D5C] hover:bg-[#0a2247]"
            onClick={handleMarkRead}
            disabled={busy || !items.length}
            data-testid="bnt-asistente-unread-mark-read"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" aria-hidden /> : null}
            Confirmar leídas
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
